import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { Toaster } from 'sonner';

import { queryClient } from '~/api/queryClient';
import { router } from '~/app/router';
import { RootErrorBoundary } from '~/components/common/error-boundary/root-error-boundary';
import { PwaInstallPrompt } from '~/components/common/pwa-install-prompt/pwa-install-prompt';
import { TooltipProvider } from '~/components/ui/tooltip';
import { TenantProvider } from '~/hoc/tenant-provider';
import { usePointerEventsCleanup } from '~/hooks/use-pointer-events-cleanup';

// Top-level React tree: wires global providers (error boundary, query client,
// tooltip, tenant theme) around the router so every route inherits them.
export function App() {
  // Strip stale `pointer-events: none` left on <body> by interrupted Radix
  // portal close cycles — without this, the whole page can go dead-click
  // until a reload. See docs/pointer-events-cleanup.md.
  usePointerEventsCleanup();

  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <TenantProvider>
            <RouterProvider router={router} />
            {/* Radix modals (Sheet, Dialog, AlertDialog) set
                `pointer-events: none` on <body> while open. That property is
                INHERITED, and the toaster is a sibling portal at body level, so
                every visible toast goes inert with the page — its close button
                and actions stop responding while a drawer is open.

                `auto` on the toast itself restores hit-testing for that subtree
                without touching the body lock, which an open overlay
                legitimately owns (`usePointerEventsCleanup` above deliberately
                leaves a live overlay's lock alone — see
                docs/pointer-events-cleanup.md).

                Sonner's only pointer-events rule is
                `[data-sonner-toast][data-visible='false']`, whose two-attribute
                selector outranks this single class, so dismissed toasts stay
                non-interactive as intended. */}
            <Toaster richColors closeButton toastOptions={{ className: 'pointer-events-auto' }} />
            <PwaInstallPrompt />
          </TenantProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}
